import { Shield, Target, Zap, Award, Crown, Users, Trophy, Star, TrendingUp, CircleDot } from "lucide-react";
import { type PlayerData, calculatePlayerMetrics } from "@/api/player";

export interface PlayerAchievement {
    id: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    unlocked: boolean;
    unlockedBy: string[];
    rarity: "common" | "rare" | "epic" | "legendary";
}

const createPlayerAchievement = (
    id: string,
    name: string,
    description: string,
    icon: React.ReactNode,
    unlocked: boolean,
    rarity: PlayerAchievement["rarity"],
    unlockedBy?: string[]
): PlayerAchievement => ({
    id,
    name,
    description,
    icon,
    unlocked,
    unlockedBy: unlockedBy || (unlocked ? ["Player"] : []),
    rarity,
});

const generateGoalkeeperAchievements = (playerData: PlayerData): PlayerAchievement[] => {
    const { goalkeeperKpis, firstName, lastName } = playerData;
    const playerName = `${firstName} ${lastName}`;

    if (!goalkeeperKpis || goalkeeperKpis.length === 0) return [];

    const avgTotalPoints = goalkeeperKpis.reduce((sum, kpi) => sum + kpi.totalPoints, 0) / goalkeeperKpis.length;
    const avgGSAx =
        goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.pointsGSAx.pointsGSAx || 0), 0) / goalkeeperKpis.length;
    const avgLowDangerPoints =
        goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.zone4.pointsLowDangerShots || 0), 0) / goalkeeperKpis.length;
    const avgHighDangerPoints =
        goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.zone1.pointsHighDangerShots || 0), 0) / goalkeeperKpis.length;

    const hasStarPerformance = goalkeeperKpis.some((kpi) => kpi.totalPoints >= 55);
    const hasEliteGSAx = goalkeeperKpis.some((kpi) => (kpi.pointsGSAx.pointsGSAx || 0) >= 15);

    const achievements = [
        createPlayerAchievement(
            "gk_elite_performance",
            "Elite Performance",
            "Average 45+ points per game",
            <Crown className="w-5 h-5" />,
            avgTotalPoints >= 45,
            "legendary",
            avgTotalPoints >= 45 ? [playerName] : []
        ),

        createPlayerAchievement(
            "gk_wall",
            "The Wall",
            "Score 55+ points in a single game",
            <Shield className="w-5 h-5" />,
            hasStarPerformance,
            "epic",
            hasStarPerformance ? [playerName] : []
        ),

        createPlayerAchievement(
            "gk_gsax_master",
            "GSAx Master",
            "Average 12+ GSAx points per game",
            <Star className="w-5 h-5" />,
            avgGSAx >= 12,
            "rare",
            avgGSAx >= 12 ? [playerName] : []
        ),

        createPlayerAchievement(
            "gk_clutch_save",
            "Clutch Saver",
            "Score 15+ GSAx points in a game",
            <Trophy className="w-5 h-5" />,
            hasEliteGSAx,
            "rare",
            hasEliteGSAx ? [playerName] : []
        ),

        createPlayerAchievement(
            "gk_danger_zone",
            "Danger Zone Defender",
            "Average 8+ high danger save points",
            <Target className="w-5 h-5" />,
            avgHighDangerPoints >= 8,
            "common",
            avgHighDangerPoints >= 8 ? [playerName] : []
        ),

        createPlayerAchievement(
            "gk_consistent",
            "Consistent Guardian",
            "Average 18+ low danger save points",
            <CircleDot className="w-5 h-5" />,
            avgLowDangerPoints >= 18,
            "common",
            avgLowDangerPoints >= 18 ? [playerName] : []
        ),
    ];

    const rarityOrder: Record<string, number> = { legendary: 4, epic: 3, rare: 2, common: 1 };

    return achievements.sort((a, b) => {
        const aUnlocked = a.unlocked;
        const bUnlocked = b.unlocked;

        if (aUnlocked === bUnlocked) {
            return rarityOrder[b.rarity] - rarityOrder[a.rarity];
        }

        return bUnlocked ? 1 : -1;
    });
};

export const generatePlayerAchievements = (playerData: PlayerData): PlayerAchievement[] => {
    const { kpis, firstName, lastName, role } = playerData;
    const playerName = `${firstName} ${lastName}`;

    if (role.includes("GK")) {
        return generateGoalkeeperAchievements(playerData);
    }

    if (!kpis || kpis.length === 0) return [];

    const metrics = calculatePlayerMetrics(kpis);

    const totalGoals = kpis.reduce((sum, kpi) => sum + kpi.bonusData.goals, 0);
    const totalAssists = kpis.reduce((sum, kpi) => sum + kpi.bonusData.assists, 0);

    const avgPassAccuracy =
        kpis.reduce((sum, kpi) => {
            const totalPasses =
                kpi.offensiveData.successfulPasses.successfulPassesData +
                kpi.offensiveData.failedPasses.failedPassesData;
            return (
                sum +
                (totalPasses > 0 ? (kpi.offensiveData.successfulPasses.successfulPassesData / totalPasses) * 100 : 0)
            );
        }, 0) / kpis.length;
    const avgTopSpeed = kpis.reduce((sum, kpi) => sum + kpi.effortData.topSpeed, 0) / kpis.length;
    const avgPlusMinusPerGame = kpis.reduce((sum, kpi) => sum + kpi.teamPlayData.plusMinusTotal, 0) / kpis.length;

    const hasHatTrick = kpis.some((kpi) => kpi.bonusData.goals >= 3);
    const hasMultiPointGame = kpis.some((kpi) => kpi.bonusData.goals + kpi.bonusData.assists >= 2);

    const achievements = [
        createPlayerAchievement(
            "point_machine",
            "Point Machine",
            "Average 55+ points per game",
            <Star className="w-5 h-5" />,
            metrics.avgPointsPerGame >= 55,
            "rare",
            metrics.avgPointsPerGame >= 55 ? [playerName] : []
        ),

        createPlayerAchievement(
            "elite_performer",
            "Elite Performer",
            "Average 70+ points per game",
            <Crown className="w-5 h-5" />,
            metrics.avgPointsPerGame >= 70,
            "legendary",
            metrics.avgPointsPerGame >= 70 ? [playerName] : []
        ),

        createPlayerAchievement(
            "goal_scorer",
            "Goal Scorer",
            "Score 2+ goals in tournament",
            <Target className="w-5 h-5" />,
            totalGoals >= 2,
            "common",
            totalGoals >= 2 ? [playerName] : []
        ),

        createPlayerAchievement(
            "playmaker",
            "Playmaker",
            "Record 8+ assists in tournament",
            <Users className="w-5 h-5" />,
            totalAssists >= 8,
            "rare",
            totalAssists >= 8 ? [playerName] : []
        ),

        createPlayerAchievement(
            "hat_trick_hero",
            "Hat Trick Hero",
            "Score 3+ goals in a single game",
            <Trophy className="w-5 h-5" />,
            hasHatTrick,
            "epic",
            hasHatTrick ? [playerName] : []
        ),

        createPlayerAchievement(
            "multi_point_master",
            "Multi-Point Master",
            "Record 2+ points in a single game",
            <Zap className="w-5 h-5" />,
            hasMultiPointGame,
            "common",
            hasMultiPointGame ? [playerName] : []
        ),

        createPlayerAchievement(
            "offensive_force",
            "Offensive Force",
            "Average 16+ offensive points per game",
            <Zap className="w-5 h-5" />,
            metrics.avgOffensivePoints >= 16,
            "rare",
            metrics.avgOffensivePoints >= 16 ? [playerName] : []
        ),

        createPlayerAchievement(
            "defensive_wall",
            "Defensive Wall",
            "Average 12+ defensive points per game",
            <Shield className="w-5 h-5" />,
            metrics.avgDefensivePoints >= 12,
            "common",
            metrics.avgDefensivePoints >= 12 ? [playerName] : []
        ),

        createPlayerAchievement(
            "team_leader",
            "Team Leader",
            "Average 20+ team play points per game",
            <Users className="w-5 h-5" />,
            metrics.avgTeamPlayPoints >= 20,
            "rare",
            metrics.avgTeamPlayPoints >= 20 ? [playerName] : []
        ),

        createPlayerAchievement(
            "precision_passer",
            "Precision Passer",
            "Maintain 75%+ pass accuracy",
            <Award className="w-5 h-5" />,
            avgPassAccuracy >= 75,
            "common",
            avgPassAccuracy >= 75 ? [playerName] : []
        ),

        createPlayerAchievement(
            "speed_demon",
            "Speed Demon",
            "Reach 9.5+ km/h top speed",
            <TrendingUp className="w-5 h-5" />,
            avgTopSpeed >= 9.5,
            "common",
            avgTopSpeed >= 9.5 ? [playerName] : []
        ),

        createPlayerAchievement(
            "team_player",
            "Team Player",
            "Maintain positive +/- rating",
            <Star className="w-5 h-5" />,
            avgPlusMinusPerGame > 0,
            "common",
            avgPlusMinusPerGame > 0 ? [playerName] : []
        ),
    ];

    const rarityOrder: Record<string, number> = { legendary: 4, epic: 3, rare: 2, common: 1 };

    return achievements.sort((a, b) => {
        const aUnlocked = a.unlocked;
        const bUnlocked = b.unlocked;

        if (aUnlocked === bUnlocked) {
            return rarityOrder[b.rarity] - rarityOrder[a.rarity];
        }

        return bUnlocked ? 1 : -1;
    });
};
